import { FC } from "react";

import { IRideRoute, MapConst } from "@ryde/shared";

import { MapLibreTrackingTrailLines } from "./maplibre-tracking-trail-lines";
import { MapLibreTrackingTrailPoints } from "./maplibre-tracking-trail-points";
import { MapLibreTrackingVehicleMarker } from "./maplibre-tracking-vehicle-marker";
import { useMapLibreTrackingRoute } from "./use-maplibre-tracking-route";

interface IMapLibreTrackingRouteProps {
  route: IRideRoute;
  showCarPositionOnly?: boolean;
  showTrackingTrail?: boolean;
  /** Below this zoom the trail arrows (and their symbol layout) are skipped. Unset = always drawn. */
  arrowsMinZoom?: number;
  /** Below this zoom no trail dots are drawn. Unset = always drawn, only thinned by decimation. */
  dotsMinZoom?: number;
}

/**
 * A tracked ride on the MapLibre map: the GPS trail it has driven, the hoverable dots along it, and the vehicle
 * at its latest fix. Reduced live-map mode keeps only the vehicle. Leaflet twin: `TrackingRoute`.
 */
export const MapLibreTrackingRoute: FC<IMapLibreTrackingRouteProps> = ({
  route,
  showCarPositionOnly = false,
  showTrackingTrail = true,
  arrowsMinZoom,
  dotsMinZoom,
}) => {
  const {
    renderState,
    carTrackingInfo,
    carPosition,
    trailId,
    trailSegments,
    markerIndices,
    trailPointColor,
    colorValue,
    inDetour,
    coursePin,
    resolvedPassengersCount,
    debugMode,
    courseId,
    handleOpenRide,
  } = useMapLibreTrackingRoute({
    route,
    showCarPositionOnly,
    showTrackingTrail,
    dotsMinZoom,
  });

  if (!renderState?.pointCount || !carPosition || !carTrackingInfo) {
    return null;
  }

  return (
    <>
      <MapLibreTrackingTrailLines
        trailId={trailId}
        segments={trailSegments}
        arrowsMinZoom={arrowsMinZoom}
      />

      {markerIndices.length > 0 && (
        <MapLibreTrackingTrailPoints
          id={`${trailId}-points`}
          renderState={renderState}
          markerIndices={markerIndices}
          color={trailPointColor}
          offsetPx={MapConst.GPS_TRAIL_OFFSET_PX}
        />
      )}

      <MapLibreTrackingVehicleMarker
        position={carPosition}
        carTrackingInfo={carTrackingInfo}
        colorValue={colorValue}
        inDetour={inDetour}
        coursePin={coursePin}
        courseId={courseId}
        passengersCount={resolvedPassengersCount}
        debugMode={debugMode}
        onOpenRide={handleOpenRide}
      />
    </>
  );
};
